import { DatePipe } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { Career } from '../../../api/app-types/career.app';
import { Client, ClientName } from '../../../api/app-types/client.app';
import { SocialSecurityStatusActivity } from '../../../api/app-types/social-security-status.app';
import { CUSTOM_DATE_FORMATS } from '../../../core/adapters/custom-date-format';
import { LvrAccordionItem } from '../../../core/components/accordion-item/accordion-item';
import { LvrAccordion } from '../../../core/components/accordion/accordion';
import { LvrHeading } from '../../../core/components/heading/heading';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { ClientResolved } from '../../../shared/routing/resolvers/client.resolver';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';

@Component({
	selector: 'lvr-client-career',
	templateUrl: './client-career.html',
	styleUrls: ['./client-career.scss'],
	imports: [
		LvrPaddingDirective,
		TranslocoPipe,
		RouterLink,
		LvrAccordion,
		LvrAccordionItem,
		DatePipe,
		LvrHeading,
		LvrButton,
	],
})
export class ClientCareer extends RouteInjection {
	private readonly session = inject(SessionManagerService);
	private readonly routeData = toSignal(this.route.data);
	public readonly canCreateCareer = hasPermission(this.session.permissions, 'Careers.Create');

	public readonly clientName = computed(() => this.routeData()?.['clientName'] as ClientName);
	public readonly dateDisplayFormat: string = CUSTOM_DATE_FORMATS.display.dateInput;
	private readonly career = computed(() => this.routeData()?.['career'] as PaginatedResponse<Career>);
	private readonly sortedCareers = computed(
		() => this.career()?.entities.sort((a, b) => b.startOfCareerStage.getTime() - a.startOfCareerStage.getTime()) ?? [],
	);
	public readonly pastCareers = computed(() => this.sortedCareers().slice(1));
	public readonly latestCareer = computed(() => this.sortedCareers()[0] ?? null);

	private readonly clientResolved = this.resolveDataAsSignal<ClientResolved>('client');
	public readonly client = computed<Client | undefined>(() => {
		const c = this.clientResolved();
		return c?.kind === 'full' ? c.value : undefined;
	});

	public getPersonNameOfWorkplaceJob(career: Career): string {
		if (!career.workplacesJob) {
			return '-';
		}
		const person = career.workplacesJob.institutionPerson;
		if (!person) return '-';

		return `${person.firstName} ${person.name}`;
	}

	public occupationData(career: Career): { title: string; institutionName: string; isSchool: boolean } {
		if (!career.workplacesJob) {
			return { title: '', institutionName: '', isSchool: false };
		}
		const job = career.workplacesJob;
		return {
			title: job.occupationTitle,
			institutionName: job.institutionBranch?.name ?? '',
			isSchool:
				career.socialSecurityStatus.statusActivity === SocialSecurityStatusActivity.S &&
				!!job.institutionBranch?.schoolNumber,
		};
	}

	public getInstitutionName(career: Career): string {
		return career.workplacesJob?.institutionBranch?.name ?? '-';
	}

	public getInstitutionRoute(career: Career): (string | number)[] | null {
		const branch = career.workplacesJob?.institutionBranch;
		if (!branch?.institutionId) return null;
		if (branch.schoolNumber || !branch.isMainInstitution) {
			return ['/desk', 'institution', branch.institutionId, 'workplace', branch.id];
		}
		return ['/desk', 'institution', branch.institutionId];
	}

	public getTypeAndScope(career: Career): string {
		return career.workplacesJob?.typeAndScopeOfTheWorkplace ?? '-';
	}

	public getOccupationWithInstitutionText(career: Career): string {
		const occupation = career.workplacesJob?.occupationTitle ?? '-';
		const institutionName = this.getInstitutionName(career);
		if (institutionName === '-') return occupation;
		return `${occupation} (${institutionName})`;
	}

	public getPersonRoute(career: Career): (string | number)[] | null {
		const personId = career.workplacesJob?.institutionPerson?.id;
		const institutionId = career.workplacesJob?.institutionBranch?.institutionId;
		if (!personId || !institutionId) return null;
		return ['/desk', 'institution', institutionId, 'persons', personId];
	}

	public getInstitutionInformedKey(career: Career): string {
		if (!career.workplacesJob) return '-';
		return career.workplacesJob.institutionInformed ? 'client.career.informed' : 'client.career.not_informed';
	}

	public isInstitutionInformedVisible(career: Career): boolean {
		return career.socialSecurityStatus.statusActivity !== SocialSecurityStatusActivity.S;
	}

	public openCreate() {
		if (!this.canCreateCareer()) return;
		this.router.navigate(['add'], { relativeTo: this.route });
	}
}
